// hooks/useCompanyData.js
import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";

export default function useCompanyData() {
  const { companyId, user, loading } = useAuth();

  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!companyId || loading) return;

    (async () => {
      const entQ = query(collection(db, "entities"), where("companyId", "==", companyId));
      const projQ = query(collection(db, "projects"), where("companyId", "==", companyId));
      const taskQ = query(collection(db, "tasks"), where("companyId", "==", companyId));
      const userQ = query(collection(db, "users"), where("companyId", "==", companyId));

      const [entSnap, projSnap, taskSnap, userSnap] = await Promise.all([
        getDocs(entQ), getDocs(projQ), getDocs(taskQ), getDocs(userQ)
      ]);

      const ents = entSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const projs = projSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const tks   = taskSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const usrs  = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // If Member, only keep own tasks
      const tksForUser =
        user?.role === "Member" ? tks.filter(t => t.assignedTo === user?.name) : tks;

      setEntities(ents);
      setProjects(projs);
      setTasks(tksForUser);
      setUsers(usrs);
    })();
  }, [companyId, loading, user?.role, user?.name]);

  // Safe counts object (never undefined)
  const counts = useMemo(() => ({
    totalEntities: new Set(entities.map(e => e.name)).size,
    totalProjects: new Set(projects.map(p => p.name)).size,
    totalTasks: tasks.length,
    userName: user?.name || "",
    userRole: user?.role || "",
  }), [entities, projects, tasks, user?.name, user?.role]);

  return { entities, projects, tasks, users, counts };
}
